import React, { useMemo, useState } from 'react';
import { CollapsiblePanel } from '@/components/common';
import {
  BILLING_INTERVALS,
  type BillingPlanDraft,
  type BillingPlanFieldErrors,
  buildBillingPlanPayload,
  validateBillingPlanDraft,
} from '@/utils/billingPlanForm';

type Props = {
  mode: 'create' | 'edit';
  draft: BillingPlanDraft;
  onChange: (draft: BillingPlanDraft) => void;
  onClose: () => void;
  onSubmit: (payload: ReturnType<typeof buildBillingPlanPayload>) => Promise<void>;
  saving?: boolean;
};

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <span className="field-error">{message}</span>;
}

function QuotaInput({
  label,
  value,
  onChange,
  error,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  error?: string;
}) {
  const unlimited = value == null;
  return (
    <label>
      {label}
      <div className="quota-input-row">
        <input
          type="number"
          min={1}
          disabled={unlimited}
          value={unlimited ? '' : value}
          onChange={(e) => onChange(Number(e.target.value) || null)}
        />
        <label className="checkbox-inline">
          <input
            type="checkbox"
            checked={unlimited}
            onChange={(e) => onChange(e.target.checked ? null : 1)}
          />
          Unlimited
        </label>
      </div>
      <FieldError message={error} />
    </label>
  );
}

export default function BillingPlanFormModal({
  mode,
  draft,
  onChange,
  onClose,
  onSubmit,
  saving = false,
}: Props) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<BillingPlanFieldErrors>({});
  const title = mode === 'create' ? 'New billing plan' : 'Edit billing plan';

  const intervalOptions = useMemo(() => BILLING_INTERVALS, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setServerError(null);
    const errors = validateBillingPlanDraft(draft, mode);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      await onSubmit(buildBillingPlanPayload(draft));
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Failed to save plan');
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <form
        className="modal panel billing-plan-form-modal"
        role="dialog"
        aria-labelledby="billing-plan-form-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => void handleSubmit(e)}
      >
        <h2 id="billing-plan-form-title">{title}</h2>
        {serverError ? <div className="alert alert-error">{serverError}</div> : null}

        <div className="billing-plan-form-sections">
          <section>
            <h3>Basic</h3>
            <div className="form-grid">
              <label>
                Name
                <input
                  required
                  value={draft.name}
                  onChange={(e) => onChange({ ...draft, name: e.target.value })}
                />
                <FieldError message={fieldErrors.name} />
              </label>
              <label>
                Slug
                <input
                  required
                  value={draft.slug}
                  onChange={(e) => onChange({ ...draft, slug: e.target.value })}
                />
                <FieldError message={fieldErrors.slug} />
              </label>
              <label className="form-grid-span-2">
                Description
                <textarea
                  rows={2}
                  value={draft.description}
                  onChange={(e) => onChange({ ...draft, description: e.target.value })}
                />
              </label>
              <label className="checkbox-inline">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => onChange({ ...draft, isActive: e.target.checked })}
                />
                Active
              </label>
            </div>
          </section>

          <section>
            <h3>Pricing</h3>
            <div className="form-grid">
              <label>
                Price (cents)
                <input
                  type="number"
                  min={0}
                  required
                  value={draft.priceCents}
                  onChange={(e) => onChange({ ...draft, priceCents: Number(e.target.value) })}
                />
                <FieldError message={fieldErrors.priceCents} />
              </label>
              <label>
                Currency
                <input
                  required
                  value={draft.currency}
                  onChange={(e) => onChange({ ...draft, currency: e.target.value })}
                />
                <FieldError message={fieldErrors.currency} />
              </label>
              <label>
                Interval
                <select
                  value={draft.billingInterval}
                  onChange={(e) => onChange({ ...draft, billingInterval: e.target.value })}
                >
                  {intervalOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <FieldError message={fieldErrors.billingInterval} />
              </label>
            </div>
          </section>

          <section>
            <h3>Quotas</h3>
            <div className="form-grid">
              <label>
                Max searches
                <input
                  type="number"
                  min={1}
                  required
                  value={draft.maxSearches}
                  onChange={(e) => onChange({ ...draft, maxSearches: Number(e.target.value) })}
                />
                <FieldError message={fieldErrors.maxSearches} />
              </label>
              <QuotaInput
                label="Max sources per search"
                value={draft.maxSourcesPerSearch}
                onChange={(value) => onChange({ ...draft, maxSourcesPerSearch: value })}
              />
              <QuotaInput
                label="Daily notification limit"
                value={draft.maxNotificationsPerDay}
                onChange={(value) => onChange({ ...draft, maxNotificationsPerDay: value })}
              />
              <QuotaInput
                label="Monthly notification limit"
                value={draft.maxNotificationsPerMonth}
                onChange={(value) => onChange({ ...draft, maxNotificationsPerMonth: value })}
              />
              <QuotaInput
                label="Scrape interval (minutes)"
                value={draft.minScrapeIntervalMinutes}
                onChange={(value) => onChange({ ...draft, minScrapeIntervalMinutes: value })}
              />
              <QuotaInput
                label="Max recipients"
                value={draft.maxNotificationRecipients}
                onChange={(value) => onChange({ ...draft, maxNotificationRecipients: value })}
              />
              <QuotaInput
                label="Max templates"
                value={draft.maxTemplates}
                onChange={(value) => onChange({ ...draft, maxTemplates: value })}
              />
            </div>
          </section>

          <section>
            <h3>Display</h3>
            <div className="form-grid">
              <label>
                Marketing label
                <input
                  value={draft.marketingTitle}
                  onChange={(e) => onChange({ ...draft, marketingTitle: e.target.value })}
                />
              </label>
              <label>
                Badge text
                <input
                  value={draft.badge}
                  onChange={(e) => onChange({ ...draft, badge: e.target.value })}
                />
              </label>
              <label>
                Sort order
                <input
                  type="number"
                  value={draft.displayOrder}
                  onChange={(e) => onChange({ ...draft, displayOrder: Number(e.target.value) })}
                />
              </label>
              <label className="checkbox-inline">
                <input
                  type="checkbox"
                  checked={draft.recommended}
                  onChange={(e) => onChange({ ...draft, recommended: e.target.checked })}
                />
                Highlighted / recommended
              </label>
            </div>
          </section>

          <CollapsiblePanel
            id="billing-plan-advanced-json"
            storageScope="billing-plan-form"
            title="Advanced config JSON"
            defaultExpanded={false}
          >
            <label className="form-grid-span-2">
              Config JSON
              <textarea
                rows={8}
                className="code-input"
                value={draft.configJsonText}
                onChange={(e) => onChange({ ...draft, configJsonText: e.target.value })}
              />
              <FieldError message={fieldErrors.configJson} />
            </label>
          </CollapsiblePanel>
        </div>

        <div className="table-action-bar">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : mode === 'create' ? 'Create plan' : 'Save changes'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
